import { useAdmin } from '../ui/Shell';
import { Banner, HBars, Kpis } from '../ui/Block';
import { describeErr, useData } from '../lib/hooks';
import { DBlock } from './dataShared';
import { CATEGORIES, CostLinesTable, catLabel, money, pct, type CostLine, type CostTotals } from './costShared';

interface TxCosts { transcriptionId: string; totals: CostTotals; lines: CostLine[]; polarFeePct: number; accountHasPaid: boolean | null; revenueIs: string }

export function CostsTab({ id }: { id: string }) {
  const ctx = useAdmin();
  const d = useData<TxCosts>(ctx.env, `/admin/transcriptions/${id}/costs`, 0, ctx.paused);
  const c = d.data;
  if (!c) {
    const err = describeErr(d.error);
    return <DBlock title="Costs" loading={d.loading} error={err ? { ...err, retry: d.reload } : undefined} />;
  }
  const t = c.totals;
  const recs = new Set(c.lines.map((l) => l.recording).filter(Boolean));
  const byCat = CATEGORIES.map((k) => ({ ...k, usd: c.lines.filter((l) => l.category === k.key).reduce((a, l) => a + l.usd, 0) })).filter((k) => k.usd > 0);
  const est = t.cost ? Math.round((t.estimated / t.cost) * 100) : 0;
  return (
    <>
      {c.accountHasPaid === false && t.revenue > 0 && (
        <DBlock bg="var(--conf-mid-soft)"><Banner icon="warn" fg="var(--conf-mid)" text="Paid from free credit" sub="This account has never paid: the revenue below is signup or granted credit, not money received." /></DBlock>
      )}
      <DBlock title="Margin" sub={`Revenue is ${c.revenueIs}.`}>
        <Kpis min={130} items={[
          { label: 'Revenue', v: money(t.revenue), sub: 'before tax' },
          { label: 'We keep', v: money(t.netRevenue), sub: `after Polar ~${Math.round(c.polarFeePct * 100)}%` },
          { label: 'Cost', v: money(t.cost), sub: t.cost ? `${est}% estimated` : 'none recorded' },
          { label: 'Margin', v: money(t.margin), fg: t.margin < 0 ? 'var(--conf-low)' : 'var(--ok)', dot: t.margin < 0 ? 'bad' : 'ok', sub: t.marginPct != null ? `${pct(t.marginPct)} of what we keep` : 'nothing kept' },
        ]} />
      </DBlock>
      {byCat.length > 0 && (
        <DBlock title="By category">
          <HBars rows={byCat.map((k) => ({ label: catLabel(k.key), n: k.usd, v: money(k.usd), bg: k.bg }))} />
        </DBlock>
      )}
      <DBlock title="Cost lines" count={String(c.lines.length)} sub="Recorded: read from what the product logged, times a list price. Estimated: an estimated unit rate times what was logged of its use."
        empty={c.lines.length ? undefined : 'Nothing ran for this transcription.'}>
        <div className="tbl--wrap"><CostLinesTable lines={c.lines} mobile={ctx.mobile} recordings={recs.size > 1} /></div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 18, padding: '10px 14px', fontSize: 12.5, borderTop: '1px solid var(--hair)' }}>
          <span style={{ color: 'var(--ink-55)' }}>Total cost</span><span className="mono" style={{ fontWeight: 600 }}>{money(t.cost)}</span>
        </div>
      </DBlock>
    </>
  );
}
